import { z } from 'zod';
import {
  EventCents,
  EventDay,
  EventFlag,
  EventKeyList,
  EventLookupId,
  EventNoQuery,
  EventOptionalEnum,
  EventRowId,
  EventText,
} from './eventPlanningFields';

// Speakers, sessions and the agenda of an in-person event (/api/events/:id/agenda, /api/events/agenda/*).
// Shape only: days of the event, times, honoraria rules and lookups stay in the agenda service.
// The lists mirror the agenda and program rules (the tests compare them).

export const EventSessionFormat = z.enum([
  'keynote',
  'panel',
  'workshop',
  'talk',
  'break',
  'meal',
  'networking',
  'other',
]);
export type EventSessionFormat = z.infer<typeof EventSessionFormat>;

export const EventPartRole = z.enum(['speaker', 'panelist', 'moderator', 'emcee']);
export type EventPartRole = z.infer<typeof EventPartRole>;

export const EventSpeakerConfirmation = z.enum(['tbc', 'confirmed', 'declined']);
export type EventSpeakerConfirmation = z.infer<typeof EventSpeakerConfirmation>;

export const EventSpeakerFormat = z.enum(['in_person', 'virtual']);
export type EventSpeakerFormat = z.infer<typeof EventSpeakerFormat>;

export const EventSpeakerHonorarium = z.enum(['none', 'paid', 'declined']);
export type EventSpeakerHonorarium = z.infer<typeof EventSpeakerHonorarium>;

export const EventSpeakerType = z.enum([
  'keynote',
  'speaker',
  'panelist',
  'workshop_lead',
  'moderator',
  'emcee',
  'elder',
  'youth_org',
  'industry',
]);
export type EventSpeakerType = z.infer<typeof EventSpeakerType>;

export const EventSpeakerPayeeType = z.enum(['honorarium_form', 'vendor_setup', 'non_resident', 'institution_invoice']);
export type EventSpeakerPayeeType = z.infer<typeof EventSpeakerPayeeType>;

export const EventElderRoute = z.enum(['honorarium', 'gift']);
export type EventElderRoute = z.infer<typeof EventElderRoute>;

export const EventElderRouteStatus = z.enum(['not_started', 'in_progress', 'done']);
export type EventElderRouteStatus = z.infer<typeof EventElderRouteStatus>;

// ---------------------------------------------------------------- params and queries

export const EventAgendaParams = z.strictObject({ id: EventRowId });
export const EventAgendaSessionParams = z.strictObject({ sessionId: EventRowId });
export const EventAgendaPartParams = z.strictObject({ partId: EventRowId });
export const EventAgendaSpeakerParams = z.strictObject({ speakerId: EventRowId });
/** Unknown kinds answer 404 from the service. */
export const EventAgendaDocumentParams = z.strictObject({ id: EventRowId, kind: z.string().min(1).max(60) });
export const EventAgendaNoQuery = EventNoQuery;

/** GET /api/events/:id/agenda/documents/:kind?lang=&day=&audience= (lang other than fr means en). */
export const EventAgendaDocumentQuery = z.strictObject({
  lang: z.string().max(10).optional(),
  day: z.string().max(40).optional(),
  audience: z.string().max(60).optional(),
});
export type EventAgendaDocumentQuery = z.input<typeof EventAgendaDocumentQuery>;

// ---------------------------------------------------------------- requests

/** POST /api/events/:id/agenda/import — the planning sheet (.xlsx, base64); dry run unless confirm is true. */
export const EventAgendaImportRequest = z.strictObject({
  fileName: z.string().max(500).nullish(),
  content: z.string().nullish(),
  sheet: z.string().max(200).nullish(),
  day: z.string().max(40).nullish(),
  confirm: z.boolean().optional(),
});
export type EventAgendaImportRequest = z.input<typeof EventAgendaImportRequest>;

const Time = z.string().max(10).nullish();

const sessionShape = {
  day: EventDay,
  startTime: Time,
  endTime: Time,
  title: EventText(),
  titleFr: EventText(),
  description: EventText(),
  descriptionFr: EventText(),
  format: EventOptionalEnum(EventSessionFormat.options, `Format is one of ${EventSessionFormat.options.join(', ')}`),
  room: EventText(),
  audiences: EventKeyList.nullish(),
  hybrid: EventFlag,
  interpreted: EventFlag,
  notes: EventText(),
  zoomLink: EventText(),
  recordingPlanned: EventFlag,
};

/** POST /api/events/:id/agenda/sessions (day, start time and title are required by the service). */
export const EventSessionRequest = z.strictObject(sessionShape);
export type EventSessionRequest = z.input<typeof EventSessionRequest>;

export const EventSessionRecording = z
  .strictObject({
    recorded: EventFlag,
    subtitlesEn: EventFlag,
    subtitlesFr: EventFlag,
    link: EventText(),
    publishedWhere: EventText(),
    notes: EventText(),
  })
  .partial();
export type EventSessionRecording = z.input<typeof EventSessionRecording>;

/** PATCH /api/events/agenda/sessions/:sessionId; only the fields sent change. */
export const EventSessionUpdateRequest = z.strictObject({
  ...sessionShape,
  recording: EventSessionRecording.nullish(),
});
export type EventSessionUpdateRequest = z.input<typeof EventSessionUpdateRequest>;

/** POST /api/events/agenda/sessions/:sessionId/move */
export const EventSessionMoveRequest = z.strictObject({
  direction: z.enum(['up', 'down'], { error: 'Direction is up or down' }),
});
export type EventSessionMoveRequest = z.input<typeof EventSessionMoveRequest>;

const RoleOf = z.enum(EventPartRole.options, { error: `Role is one of ${EventPartRole.options.join(', ')}` });

/** POST /api/events/agenda/sessions/:sessionId/speakers */
export const EventSessionSpeakerRequest = z.strictObject({
  speakerId: z.string().max(200).nullish(),
  role: RoleOf.optional(),
});
export type EventSessionSpeakerRequest = z.input<typeof EventSessionSpeakerRequest>;

const Deliverables = z
  .strictObject({
    draftDeck: EventFlag,
    finalDeck: EventFlag,
    glossary: EventFlag,
    prepMeeting: EventFlag,
    deckLink: EventText(),
    glossaryLink: EventText(),
    dinnerRsvp: z.string().max(10).nullish(),
    consentToRecord: EventFlag,
  })
  .partial();

const Commitment = z
  .strictObject({
    dietary: EventText(),
    schedule: EventText(),
    arrival: EventText(),
    departure: EventText(),
    accessibility: EventText(),
  })
  .partial();

const Engagement = z
  .strictObject({
    invitationNotes: EventText(),
    timing: EventText(),
    protocol: EventText(),
    honorariumAgreed: EventFlag,
    route: EventOptionalEnum(EventElderRoute.options, `Payment route is one of ${EventElderRoute.options.join(', ')}`),
    routeStatus: EventOptionalEnum(
      EventElderRouteStatus.options,
      `Route status is one of ${EventElderRouteStatus.options.join(', ')}`,
    ),
  })
  .partial();

const speakerShape = {
  displayName: EventText(),
  title: EventText(),
  affiliation: EventText(),
  bio: EventText(),
  bioFr: EventText(),
  photo: EventText(),
  notes: EventText(),
  confirmation: z.enum(EventSpeakerConfirmation.options, {
    error: `Confirmation is one of ${EventSpeakerConfirmation.options.join(', ')}`,
  }),
  format: EventOptionalEnum(EventSpeakerFormat.options, 'Format is in_person or virtual'),
  hotelNeeded: EventFlag,
  honorarium: z.enum(EventSpeakerHonorarium.options, {
    error: `Honorarium is one of ${EventSpeakerHonorarium.options.join(', ')}`,
  }),
  honorariumCents: EventCents,
  invited: EventFlag,
  approvalSent: EventFlag,
  pheedloop: EventFlag,
  speakerType: EventOptionalEnum(
    EventSpeakerType.options,
    `Speaker type is one of ${EventSpeakerType.options.join(', ')}`,
  ),
  payeeType: EventOptionalEnum(
    EventSpeakerPayeeType.options,
    `Payee type is one of ${EventSpeakerPayeeType.options.join(', ')}`,
  ),
  deliverables: Deliverables.nullish(),
  commitment: Commitment.nullish(),
  engagement: Engagement.nullish(),
};

/** PATCH /api/events/agenda/speakers/:speakerId; only the fields sent change. */
export const EventSpeakerUpdateRequest = z.strictObject(speakerShape).partial();
export type EventSpeakerUpdateRequest = z.input<typeof EventSpeakerUpdateRequest>;

/** POST /api/events/:id/agenda/speakers — a member, a contact, an email or a new contact; optionally onto a session. */
export const EventSpeakerRequest = z
  .strictObject({
    ...speakerShape,
    userId: EventLookupId,
    contactId: EventLookupId,
    email: z.string().max(320).nullish(),
    name: EventText(),
    newContact: z
      .strictObject({
        name: EventText(),
        email: z.string().max(320).nullish(),
        organization: EventText(),
        title: EventText(),
      })
      .partial()
      .nullish(),
    sessionId: EventLookupId,
    role: z.union([RoleOf, z.literal(''), z.null()]),
  })
  .partial();
export type EventSpeakerRequest = z.input<typeof EventSpeakerRequest>;

/** POST /api/events/agenda/speakers/:speakerId/invite (the core audience unless told otherwise). */
export const EventSpeakerInviteRequest = z.strictObject({ audienceKey: z.string().max(60).nullish() });
export type EventSpeakerInviteRequest = z.input<typeof EventSpeakerInviteRequest>;
